import { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { Top149ProvinceYearStatsSchema } from '../../../services/top149Api';
import { z } from 'zod';

type Top149ProvinceStats = z.infer<typeof Top149ProvinceYearStatsSchema>;

interface UseTop149ProvincesColumnsProps {
	provinceStats?: { stats: Top149ProvinceStats[] };
	year?: number;
}

export function useTop149ProvincesColumns({ provinceStats, year }: UseTop149ProvincesColumnsProps) {
	return useMemo<GridColDef[]>(
		() => {
			const baseColumns: GridColDef[] = [
				{
					field: 'stt',
					headerName: 'STT',
					width: 60,
					headerAlign: 'center',
					align: 'center',
					sortable: false,
					filterable: false,
					renderCell: ({ api, id }) => {
						const allRows = api.getRowModels();
						const rowIndex = Array.from(allRows.keys()).indexOf(id as string);
						return rowIndex + 1;
					},
				},
				{
					field: 'maTinh',
					headerName: 'Mã Tỉnh',
					width: 150,
					headerAlign: 'center',
					align: 'center',
					sortable: true,
					filterable: false
				},
				{
					field: 'tenTinh',
					headerName: 'Tên Tỉnh',
					width: 300,
					headerAlign: 'center',
					sortable: true,
					filterable: false,
				},
				{
					field: 'soTruong',
					headerName: 'Số Trường',
					width: 100,
					type: 'number',
					headerAlign: 'center',
					align: 'center',
					filterable: false,
					sortable: true,
				},
			];

			const yearColumns: GridColDef[] = [];
			if (year && provinceStats?.stats) {
				const provinceStatsMap = new Map<string, Map<string, number>>();

				provinceStats.stats.forEach((provinceStat: Top149ProvinceStats) => {
					if (!provinceStatsMap.has(provinceStat.maTinh)) {
						provinceStatsMap.set(provinceStat.maTinh, new Map());
					}
					const yearDataMap = provinceStatsMap.get(provinceStat.maTinh)!;
					Object.entries(provinceStat.yearData).forEach(([yearKey, count]) => {
						yearDataMap.set(yearKey, count as number);
					});
				});

				yearColumns.push({
					field: year.toString(),
					headerName: `Số sinh viên năm ${year}`,
					width: 100,
					type: 'number',
					headerAlign: 'center',
					sortable: true,
					filterable: false,
					align: 'center',
					renderCell: ({ row }: { row: Top149ProvinceStats }) => {
						const yearDataMap = provinceStatsMap.get(row.maTinh);
						const count = yearDataMap ? yearDataMap.get(year.toString()) || 0 : 0;
						return count;
					},
				});
			}

			return [...baseColumns, ...yearColumns];
		},
		[year, provinceStats],
	);
}
